'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, ChevronDown, MessageCircle } from 'lucide-react';

const navItems = [
  {
    label: 'Private Umrah',
    key: 'nav-private-umrah',
    children: [
      { label: 'Couple Umrah', href: '/public-website-homepage#private-umrah', key: 'nav-couple' },
      { label: 'Family Umrah', href: '/public-website-homepage#private-umrah', key: 'nav-family' },
      { label: 'Elderly Umrah', href: '/public-website-homepage#private-umrah', key: 'nav-elderly' },
      { label: 'Executive Umrah', href: '/public-website-homepage#private-umrah', key: 'nav-executive' },
      { label: 'Bespoke Journey', href: '/public-website-homepage#private-umrah', key: 'nav-bespoke' },
    ],
  },
  {
    label: 'Rancang Perjalanan',
    key: 'nav-plan',
    children: [
      { label: 'Journey Builder', href: '/journey-builder', key: 'nav-design' },
      { label: 'Cara Kerja', href: '/public-website-homepage#how-it-works', key: 'nav-how' },
    ],
  },
  {
    label: 'Layanan',
    key: 'nav-services',
    children: [
      { label: 'Hotel', href: '/public-website-homepage#hotel-showcase', key: 'nav-hotel' },
      { label: 'Penerbangan', href: '/public-website-homepage#layanan', key: 'nav-flight' },
      { label: 'Visa Umrah', href: '/public-website-homepage#layanan', key: 'nav-visa' },
      { label: 'Transport', href: '/public-website-homepage#layanan', key: 'nav-transport' },
      { label: 'Muttawif', href: '/public-website-homepage#layanan', key: 'nav-muttawif' },
      { label: 'Add-ons', href: '/public-website-homepage#layanan', key: 'nav-addons' },
    ],
  },
  {
    label: 'Destinasi',
    key: 'nav-destination',
    children: [
      { label: 'Makkah', href: '/public-website-homepage#destination', key: 'nav-makkah' },
      { label: 'Madinah', href: '/public-website-homepage#destination', key: 'nav-madinah' },
      { label: 'Saudi Arabia', href: '/public-website-homepage#destination', key: 'nav-saudi' },
    ],
  },
  { label: 'Journal', key: 'nav-journal', href: '/public-website-homepage#journal' },
  {
    label: 'Tentang',
    key: 'nav-about',
    children: [
      { label: 'Tentang SAFARUNA', href: '/public-website-homepage#about', key: 'nav-about-us' },
      { label: 'Mengapa SAFARUNA', href: '/public-website-homepage#why-safaruna', key: 'nav-why' },
      { label: 'Concierge', href: '/public-website-homepage#concierge', key: 'nav-concierge' },
      { label: 'FAQ', href: '/public-website-homepage#faq', key: 'nav-faq' },
    ],
  },
];

export default function PublicNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileSection = (key: string) => {
    setMobileExpanded((prev) => (prev === key ? null : key));
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border' : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link href="/public-website-homepage" className="flex items-center gap-2.5">
              <AppLogo size={36} />
              <span className={`font-bold text-lg tracking-tight transition-colors duration-300 ${scrolled ? 'text-primary' : 'text-white'}`}>
                SAFARUNA
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems?.map((item) => (
                <div key={item?.key} className="relative group">
                  {item?.children ? (
                    <>
                      <button
                        className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                          scrolled ? 'text-foreground hover:text-primary hover:bg-muted' : 'text-white/90 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        {item?.label}
                        <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
                      </button>
                      <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-2xl shadow-xl border border-border py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-1 group-hover:translate-y-0 z-50">
                        {item?.children?.map((child) => (
                          <Link
                            key={child?.key}
                            href={child?.href}
                            className="block px-4 py-2.5 text-sm text-foreground hover:text-primary hover:bg-muted/60 transition-colors duration-150"
                          >
                            {child?.label}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Link
                      href={item?.href || '#'}
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                        scrolled ? 'text-foreground hover:text-primary hover:bg-muted' : 'text-white/90 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      {item?.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                href="/sign-up-login-screen"
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                  scrolled ? 'text-foreground hover:text-primary' : 'text-white/80 hover:text-white'
                }`}
              >
                Masuk
              </Link>
              <a
                href="https://wa.me/6285647509693?text=Assalamu'alaikum%20SAFARUNA%2C%20saya%20ingin%20konsultasi%20mengenai%20perjalanan%20Umrah."
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                  scrolled ? 'text-green-600 hover:bg-green-50' : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                <MessageCircle size={15} />
                Konsultasi
              </a>
              <Link
                href="/journey-builder"
                className="btn-accent text-sm px-5 py-2.5"
              >
                Rancang Perjalanan
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className={`lg:hidden p-2 rounded-lg transition-colors ${scrolled ? 'text-foreground' : 'text-white'}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Tutup menu' : 'Buka menu'}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Drawer */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-border animate-fade-in max-h-[80vh] overflow-y-auto">
            <nav className="px-4 py-3 space-y-1">
              {navItems?.map((item) => (
                <div key={item?.key}>
                  {item?.children ? (
                    <div>
                      <button
                        onClick={() => toggleMobileSection(item?.key)}
                        className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground hover:text-primary hover:bg-muted/50 rounded-xl transition-colors"
                      >
                        <span>{item?.label}</span>
                        <ChevronDown
                          size={16}
                          className={`text-muted-foreground transition-transform duration-200 ${mobileExpanded === item?.key ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {mobileExpanded === item?.key && (
                        <div className="ml-4 mt-1 space-y-0.5 animate-fade-in">
                          {item?.children?.map((child) => (
                            <Link
                              key={child?.key}
                              href={child?.href}
                              className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-primary hover:bg-muted/40 rounded-lg transition-colors"
                              onClick={() => setMobileOpen(false)}
                            >
                              {child?.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      href={item?.href || '#'}
                      className="block px-3 py-3 text-sm font-semibold text-foreground hover:text-primary hover:bg-muted/50 rounded-xl transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      {item?.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Mobile CTAs */}
            <div className="px-4 pb-4 pt-2 border-t border-border space-y-2">
              <Link
                href="/journey-builder"
                className="btn-accent w-full text-center text-sm py-3"
                onClick={() => setMobileOpen(false)}
              >
                Rancang Perjalanan
              </Link>
              <a
                href="https://wa.me/6285647509693?text=Assalamu'alaikum%20SAFARUNA%2C%20saya%20ingin%20konsultasi%20mengenai%20perjalanan%20Umrah."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-green-500 text-green-600 text-sm font-semibold hover:bg-green-50 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <MessageCircle size={16} />
                Konsultasi via WhatsApp
              </a>
              <Link
                href="/sign-up-login-screen"
                className="block text-center w-full py-3 rounded-xl border border-border text-foreground text-sm font-medium hover:bg-muted/50 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Masuk
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Sticky Bottom CTA Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-border px-4 py-3 flex gap-3">
        <Link
          href="/journey-builder"
          className="flex-1 btn-accent text-center text-sm py-3"
        >
          Rancang Perjalanan
        </Link>
        <a
          href="https://wa.me/6285647509693?text=Assalamu'alaikum%20SAFARUNA%2C%20saya%20ingin%20konsultasi%20mengenai%20perjalanan%20Umrah."
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl border border-green-500 text-green-600 text-sm font-semibold hover:bg-green-50 transition-colors"
        >
          <MessageCircle size={16} />
          WA
        </a>
      </div>
    </>
  );
}